'use memo';

import { useCallback } from 'react';
import { calcMinTop, calcNextTop } from '../utils/bidirectionalSticky';

/**
 * 最寄りのスクロール可能な祖先要素を探す。
 *
 * `element.closest()` は CSS セレクタでの祖先探索しかできず「overflow-y が
 * auto/scroll の要素」を条件にできないため、親を辿って `getComputedStyle` で
 * 判定する。見つからない場合は null を返す（呼び出し側で window にフォールバック）。
 *
 * `document.documentElement` / `document.body` に到達したら null を返す。
 * 将来 `html { overflow-y: auto }` が付いた場合、ビューポートスクロールの
 * scroll イベントは `<html>` 要素をイベント経路に含まない（`document` /
 * `window` に発火する）ため、`<html>` に listener を張ると一度も発火しない。
 */
function findScrollportElement(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element.parentElement;
  while (current) {
    if (current === document.documentElement || current === document.body) {
      return null;
    }
    const overflowY = getComputedStyle(current).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * scroll listener を登録し、解除用の関数を返す。
 *
 * `HTMLElement | Window` の union に対して `addEventListener` を直接呼ぶと
 * 双方のオーバーロード解決に失敗しうるため、共通基底の `EventTarget` を引数に
 * 取るローカル関数を経由する（`as` を使わずに union を解消するため）。
 */
function subscribeScroll(target: EventTarget, handler: () => void): () => void {
  target.addEventListener('scroll', handler, {
    passive: true
  });
  return () => target.removeEventListener('scroll', handler);
}

/**
 * sticky 要素を「双方向 sticky」にするための callback ref を返すフック。
 *
 * CSS の `position: sticky` は単純な `top` 固定だと下スクロール時にしか
 * 追従できない（上スクロールしても、自然位置まで戻るまで貼り付いたまま）。
 * そのため scroll イベントのスクロール差分で `top` を
 * `minTop`（要素がスクロールポートより長い場合に負になる下端固定値）から
 * `0`（上端固定値）の間で直接書き換え、双方向の追従を実現する。
 *
 * 余白（従来の `top-8` 相当）は **対象要素自身の縦 padding** が担う。padding は
 * `offsetHeight` に含まれるため、`top = 0`（border-box 上端＝スクロールポート
 * 上端）でも中身は padding 分だけ下に見え、`top = minTop`（border-box 下端＝
 * スクロールポート下端）でも padding 分だけ上に見える。よって JS 側の上限は
 * `0`、下限は `scrollportHeight - offsetHeight` となり、設計値（32px 等）が
 * 計算式から完全に消える。余白の正は Tailwind の `py-*` のみ。
 *
 * パフォーマンスのため React state は使わず、`requestAnimationFrame` で
 * 1 フレーム 1 回に束ねて `element.style.top` を直接書き換える（imperative DOM
 * 操作）。算出値が前回と同じ場合は書き込まない（強制同期レイアウトと
 * ResizeObserver ループ警告の回避）。
 *
 * ブラウザバック等でスクロール位置が復元される場合（router の
 * `scrollRestoration: true`）に「ページ中腹なのに上端固定」とならないよう、
 * 初期 `top` は現在の scrollTop から算出する。
 *
 * ## 既知の制約
 *
 * - **祖先の単一スクロールポートを前提とする**。兄弟要素が実スクローラになる
 *   構造では機能しない（scroll イベントはバブルしないため、祖先しか辿らない
 *   本フックには届かない）。
 * - **React 19 専用**。クリーンアップ関数を返す callback ref に依存する。
 *
 * ## なぜ callback ref か（`useRef` + `useEffect([])` ではない理由）
 *
 * 対象要素は `isLoading` / `showNavigation` 等による条件付きレンダーであり、
 * `useEffect([])` では要素の mount/unmount のタイミングに追従できない
 * （マウント後に初めて描画された・途中で消えた、を検知できない）。
 * callback ref なら要素の出入りごとに登録・解除が正しく走る。
 *
 * ## `'use memo'` について
 *
 * React Compiler（annotation mode）の対象とする。中身は `useCallback` の
 * 依存なし1本のみで最適化余地は小さいが、呼び出し側
 * （`CFormSectionDetailLayout.tsx`）が `'use memo'` 済みのため粒度を揃える。
 */
export function useBidirectionalStickyRef() {
  return useCallback((element: HTMLElement | null) => {
    // React 19 ではクリーンアップ関数を返す callback ref に対して ref(null) が
    // 呼ばれないため、この分岐は実質到達しない（型都合のガード）。
    if (!element) return;
    const scrollport = findScrollportElement(element);
    const scrollTarget: HTMLElement | Window = scrollport ?? window;
    const getScrollTop = () => scrollport ? scrollport.scrollTop : document.documentElement.scrollTop;
    const getScrollportHeight = () => scrollport ? scrollport.clientHeight : document.documentElement.clientHeight;
    let minTop = calcMinTop({
      scrollportHeight: getScrollportHeight(),
      navHeight: element.offsetHeight
    });
    let lastScrollTop = getScrollTop();
    // スクロール位置復元を考慮した初期値（clamp(-scrollTop, minTop, 0)）。
    let topPx = calcNextTop({
      prevTop: 0,
      delta: lastScrollTop,
      minTop
    });
    let appliedTopPx: number | null = null;
    let rafId: number | null = null;
    const applyTop = () => {
      if (appliedTopPx === topPx) return;
      appliedTopPx = topPx;
      element.style.top = `${topPx}px`;
    };
    const handleResize = () => {
      minTop = calcMinTop({
        scrollportHeight: getScrollportHeight(),
        navHeight: element.offsetHeight
      });
      topPx = calcNextTop({
        prevTop: topPx,
        delta: 0,
        minTop
      });
      applyTop();
    };
    const handleScroll = () => {
      // 1 フレーム 1 回にバッチング（scroll は連続発火する）
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const scrollTop = getScrollTop();
        const delta = scrollTop - lastScrollTop;
        lastScrollTop = scrollTop;
        topPx = calcNextTop({
          prevTop: topPx,
          delta,
          minTop
        });
        applyTop();
      });
    };
    applyTop();
    const unsubscribeScroll = subscribeScroll(scrollTarget, handleScroll);
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(element);
    // window フォールバック時もウィンドウリサイズ / ズームで minTop を
    // 再計算するため documentElement を observe する。
    resizeObserver.observe(scrollport ?? document.documentElement);
    return () => {
      unsubscribeScroll();
      resizeObserver.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      // StrictMode の ref detach → 再 attach で inline style が残らないように戻す
      element.style.top = '';
    };
  }, []);
}